/* eslint-disable max-lines -- Strict product descriptor validators stay co-located with their closed union. */
import type { FmProductPageProps } from './FmProductPage';
import type {
  FmProductActionDescriptor,
  FmProductHeroDescriptor,
} from './FmProductPage.types';
import { validateSection as validateFmSection } from './sections/section.validation';
import { assertArchetypePaceCapabilities } from './sections/paceCapabilities';
import { FM_PRODUCT_CAPABILITIES } from './FmProductPage.types';
import { validateFmProductChrome } from './FmProductChrome.validation';
import { assertFmEvidenceDescriptor } from './FmEvidence.validation';
import { getMediaAsset, hasMediaAsset } from '@/shared/config/mediaRegistry';

type UnknownRecord = Record<string, unknown>;

const AVAILABLE_STATES = new Set(['ready', 'partial', 'stale']);
const SECTION_ROLES = ['lead', 'evidence', 'detail', 'action'] as const;
const CONVERSION_ROLES = new Set(['orient', 'build-trust', 'compare', 'convert', 'handoff']);

function fail(path: string, message: string): never {
  throw new Error(`FmProductPage: ${path} ${message}`);
}

function record(value: unknown, path: string): UnknownRecord {
  if (!value || typeof value !== 'object' || Array.isArray(value)) {
    fail(path, 'must be an object');
  }
  return value as UnknownRecord;
}

function exact(value: UnknownRecord, allowed: readonly string[], path: string): void {
  const unexpected = Object.keys(value).filter((key) => !allowed.includes(key));
  if (unexpected.length > 0) fail(path, `contains unknown key "${unexpected[0]}"`);
}

function string(value: unknown, path: string): string {
  if (typeof value !== 'string' || value.trim() === '') fail(path, 'must be a non-empty string');
  return value;
}

function optionalString(value: unknown, path: string): void {
  if (value !== undefined) string(value, path);
}

function finiteNumber(value: unknown, path: string): number {
  if (typeof value !== 'number' || !Number.isFinite(value)) fail(path, 'must be a finite number');
  return value;
}

function array(value: unknown, path: string, minimum = 1): unknown[] {
  if (!Array.isArray(value) || value.length < minimum) {
    fail(path, `must contain at least ${minimum} item${minimum === 1 ? '' : 's'}`);
  }
  return value;
}

function scalar(value: unknown, path: string): void {
  if (typeof value !== 'string' && typeof value !== 'number') {
    fail(path, 'must be a string or number');
  }
  if (typeof value === 'number') finiteNumber(value, path);
}

function registerId(value: unknown, path: string, ids: Set<string>): string {
  const id = string(value, path);
  if (ids.has(id)) fail(path, `duplicates id "${id}"`);
  ids.add(id);
  return id;
}

function validateAction(value: unknown, path: string, ids: Set<string>): void {
  const action = record(value, path);
  const disabled = action.disabledReason !== undefined;
  exact(
    action,
    disabled ? ['id', 'label', 'intent', 'disabledReason'] : ['id', 'label', 'intent', 'onSelect'],
    path,
  );
  registerId(action.id, `${path}.id`, ids);
  string(action.label, `${path}.label`);
  if (!['primary', 'secondary', 'tertiary'].includes(String(action.intent))) {
    fail(`${path}.intent`, 'must be primary, secondary, or tertiary');
  }
  if (disabled) {
    string(action.disabledReason, `${path}.disabledReason`);
  } else if (typeof action.onSelect !== 'function') {
    fail(`${path}.onSelect`, 'must be a callback');
  }
}

function validateState(value: unknown, path: string, ids: Set<string>): string {
  const state = record(value, path);
  const status = string(state.status, `${path}.status`);
  switch (status) {
    case 'ready':
      exact(state, ['status'], path);
      break;
    case 'loading':
      exact(state, ['status', 'label', 'detail'], path);
      string(state.label, `${path}.label`);
      optionalString(state.detail, `${path}.detail`);
      break;
    case 'empty':
    case 'partial':
      exact(state, ['status', 'label', 'detail'], path);
      string(state.label, `${path}.label`);
      string(state.detail, `${path}.detail`);
      break;
    case 'stale':
      exact(state, ['status', 'label', 'detail', 'updatedAt'], path);
      string(state.label, `${path}.label`);
      string(state.detail, `${path}.detail`);
      string(state.updatedAt, `${path}.updatedAt`);
      break;
    case 'locked':
      exact(state, ['status', 'label', 'detail', 'action'], path);
      string(state.label, `${path}.label`);
      string(state.detail, `${path}.detail`);
      validateAction(state.action, `${path}.action`, ids);
      break;
    case 'error':
      exact(state, ['status', 'label', 'detail', 'retry'], path);
      string(state.label, `${path}.label`);
      string(state.detail, `${path}.detail`);
      if (state.retry !== undefined) validateAction(state.retry, `${path}.retry`, ids);
      break;
    default:
      fail(`${path}.status`, 'must be ready, loading, empty, partial, stale, locked, or error');
  }
  return status;
}

function validateFacts(value: unknown, path: string, ids: Set<string>): void {
  array(value, path).forEach((entry, index) => {
    const itemPath = `${path}[${index}]`;
    const fact = record(entry, itemPath);
    exact(fact, ['id', 'label', 'value', 'unit'], itemPath);
    registerId(fact.id, `${itemPath}.id`, ids);
    string(fact.label, `${itemPath}.label`);
    scalar(fact.value, `${itemPath}.value`);
    optionalString(fact.unit, `${itemPath}.unit`);
  });
}

function validateContextMedia(value: unknown, path: string): void {
  const context = record(value, path);
  exact(
    context,
    [
      'scope',
      'species',
      'developmentClass',
      'landClass',
      'structure',
      'condition',
      'reportedCause',
      'basisLabel',
      'sourceRevision',
      'freshness',
      'actualMedia',
      'allowIllustrativeFallback',
      'stateId',
    ],
    path,
  );
  if (!['editorial', 'property', 'stand'].includes(String(context.scope))) {
    fail(`${path}.scope`, 'must be editorial, property, or stand');
  }
  if (
    context.species !== undefined &&
    !['pine', 'spruce', 'birch', 'deciduous', 'mixed', 'other', 'unknown'].includes(
      String(context.species),
    )
  ) {
    fail(`${path}.species`, 'is not a registered semantic species');
  }
  if (
    context.developmentClass !== undefined &&
    !['A0', 'T1', 'T2', 'unknown'].includes(String(context.developmentClass))
  ) {
    fail(`${path}.developmentClass`, 'is not registered');
  }
  ['landClass', 'structure', 'condition', 'reportedCause'].forEach((key) => {
    if (context[key] !== undefined) string(context[key], `${path}.${key}`);
  });
  ['basisLabel', 'sourceRevision', 'freshness', 'stateId'].forEach((key) =>
    optionalString(context[key], `${path}.${key}`),
  );
  if (typeof context.allowIllustrativeFallback !== 'boolean') {
    fail(`${path}.allowIllustrativeFallback`, 'must be a boolean');
  }
  if (context.actualMedia !== undefined) {
    const actual = record(context.actualMedia, `${path}.actualMedia`);
    exact(actual, ['identity', 'evidenceKind'], `${path}.actualMedia`);
    const identity = string(actual.identity, `${path}.actualMedia.identity`);
    if (!hasMediaAsset(identity)) fail(`${path}.actualMedia.identity`, 'must be registered');
    if (
      ![
        'field-image',
        'inspection-map',
        'orthophoto',
        'canopy-raster',
        'property-image',
        'other',
      ].includes(String(actual.evidenceKind))
    ) {
      fail(`${path}.actualMedia.evidenceKind`, 'is not registered');
    }
  }
}

function validateRegisteredVideoMedia(
  section: UnknownRecord,
  path: string,
  ids: Set<string>,
  mediaIdentity: string,
): void {
  const posterIdentity = string(section.posterIdentity, `${path}.posterIdentity`);
  if (!hasMediaAsset(posterIdentity)) fail(`${path}.posterIdentity`, 'must be registered');
  if (getMediaAsset(mediaIdentity).kind !== 'video')
    fail(`${path}.mediaIdentity`, 'must reference a registered video');
  if (getMediaAsset(posterIdentity).kind !== 'poster')
    fail(`${path}.posterIdentity`, 'must reference a registered poster');
  if (
    (section.mobileMediaIdentity === undefined) !==
    (section.mobilePosterIdentity === undefined)
  ) {
    fail(path, 'must provide mobileMediaIdentity and mobilePosterIdentity together');
  }
  if (section.mobileMediaIdentity !== undefined) {
    const mobileMediaIdentity = string(section.mobileMediaIdentity, `${path}.mobileMediaIdentity`);
    const mobilePosterIdentity = string(
      section.mobilePosterIdentity,
      `${path}.mobilePosterIdentity`,
    );
    if (getMediaAsset(mobileMediaIdentity).kind !== 'video')
      fail(`${path}.mobileMediaIdentity`, 'must reference a registered video');
    if (getMediaAsset(mobilePosterIdentity).kind !== 'poster')
      fail(`${path}.mobilePosterIdentity`, 'must reference a registered poster');
  }
  ['playLabel', 'pauseLabel', 'transcriptLabel', 'transcriptTitle'].forEach((key) =>
    string(section[key], `${path}.${key}`),
  );
  array(section.transcriptScenes, `${path}.transcriptScenes`, 1).forEach((entry, index) => {
    const scenePath = `${path}.transcriptScenes[${index}]`;
    const scene = record(entry, scenePath);
    exact(scene, ['id', 'timeLabel', 'title', 'detail'], scenePath);
    ['id', 'timeLabel', 'title', 'detail'].forEach((key) =>
      string(scene[key], `${scenePath}.${key}`),
    );
    registerId(scene.id, `${scenePath}.id`, ids);
  });
}

function validateRegisteredMedia(section: UnknownRecord, path: string, ids: Set<string>): void {
  string(section.answer, `${path}.answer`);
  optionalString(section.explanation, `${path}.explanation`);
  string(section.caption, `${path}.caption`);
  const mediaIdentity = string(section.mediaIdentity, `${path}.mediaIdentity`);
  if (!hasMediaAsset(mediaIdentity)) fail(`${path}.mediaIdentity`, 'must be registered');
  if (section.mediaKind === 'video') {
    validateRegisteredVideoMedia(section, path, ids, mediaIdentity);
    return;
  }
  if (
    section.presentation !== undefined &&
    !['balanced', 'balanced-media-first', 'proof-dominant'].includes(String(section.presentation))
  ) {
    fail(`${path}.presentation`, 'must be balanced, balanced-media-first or proof-dominant');
  }
  if (section.mobileMediaIdentity !== undefined) {
    const mobileIdentity = string(section.mobileMediaIdentity, `${path}.mobileMediaIdentity`);
    if (!hasMediaAsset(mobileIdentity)) fail(`${path}.mobileMediaIdentity`, 'must be registered');
  }
  if ((section.contextMedia === undefined) !== (section.contextCaption === undefined)) {
    fail(path, 'must provide contextMedia and contextCaption together');
  }
  if (section.contextMedia !== undefined) {
    validateContextMedia(section.contextMedia, `${path}.contextMedia`);
    string(section.contextCaption, `${path}.contextCaption`);
  }
  validateFacts(section.facts, `${path}.facts`, ids);
}

function validateActions(section: UnknownRecord, path: string, ids: Set<string>): void {
  string(section.answer, `${path}.answer`);
  string(section.context, `${path}.context`);
  const actions = array(section.actions, `${path}.actions`);
  if (actions.length > 4) fail(`${path}.actions`, 'must contain at most 4 actions');
  actions.forEach((action, index) => validateAction(action, `${path}.actions[${index}]`, ids));
}

function validateCatalog(section: UnknownRecord, path: string, ids: Set<string>): void {
  string(section.answer, `${path}.answer`);
  const catalog = record(section.catalog, `${path}.catalog`);
  exact(
    catalog,
    [
      'recordId',
      'catalogVersion',
      'offerLabel',
      'priceLabel',
      'priceStatus',
      'prominence',
      'audience',
      'groupLabel',
      'inheritanceLabel',
      'scopeLabel',
      'billingLabel',
      'entitlementLabel',
      'includes',
      'relationship',
    ],
    `${path}.catalog`,
  );
  ['recordId', 'offerLabel', 'priceLabel', 'billingLabel', 'entitlementLabel'].forEach((key) =>
    string(catalog[key], `${path}.catalog.${key}`),
  );
  optionalString(catalog.catalogVersion, `${path}.catalog.catalogVersion`);
  optionalString(catalog.groupLabel, `${path}.catalog.groupLabel`);
  optionalString(catalog.inheritanceLabel, `${path}.catalog.inheritanceLabel`);
  optionalString(catalog.scopeLabel, `${path}.catalog.scopeLabel`);
  if (
    catalog.audience !== undefined &&
    !['owner', 'transaction'].includes(String(catalog.audience))
  ) {
    fail(`${path}.catalog.audience`, 'must be owner or transaction');
  }
  if (!['available', 'unavailable'].includes(String(catalog.priceStatus))) {
    fail(`${path}.catalog.priceStatus`, 'must be available or unavailable');
  }
  if (!['primary', 'secondary'].includes(String(catalog.prominence))) {
    fail(`${path}.catalog.prominence`, 'must be primary or secondary');
  }
  array(catalog.includes, `${path}.catalog.includes`).forEach((item, index) =>
    string(item, `${path}.catalog.includes[${index}]`),
  );
  if (catalog.relationship !== undefined) {
    const relationship = record(catalog.relationship, `${path}.catalog.relationship`);
    exact(relationship, ['label', 'targetRecordId', 'targetLabel'], `${path}.catalog.relationship`);
    ['label', 'targetRecordId', 'targetLabel'].forEach((key) =>
      string(relationship[key], `${path}.catalog.relationship.${key}`),
    );
  }
  validateAction(section.primaryAction, `${path}.primaryAction`, ids);
  if ((section.primaryAction as FmProductActionDescriptor).intent !== 'primary') {
    fail(`${path}.primaryAction.intent`, 'must be primary');
  }
  if (section.secondaryAction !== undefined) {
    validateAction(section.secondaryAction, `${path}.secondaryAction`, ids);
  }
}

const PAYLOAD_KEYS: Record<string, readonly string[]> = {
  'registered-media': [
    'answer',
    'explanation',
    'caption',
    'mediaIdentity',
    'mobileMediaIdentity',
    'presentation',
    'contextMedia',
    'contextCaption',
    'facts',
    'mediaKind',
    'posterIdentity',
    'mobilePosterIdentity',
    'playLabel',
    'pauseLabel',
    'transcriptLabel',
    'transcriptTitle',
    'transcriptScenes',
  ],
  'contextual-actions': ['answer', 'context', 'actions'],
  'catalog-decision-cta': ['answer', 'catalog', 'primaryAction', 'secondaryAction'],
};

function validateRole(section: UnknownRecord, path: string): void {
  if (!SECTION_ROLES.includes(section.role as (typeof SECTION_ROLES)[number])) {
    fail(`${path}.role`, 'must be lead, evidence, detail, or action');
  }
}

/** A shared section owns its state and content; the product section only names it. */
function validateSharedSection(section: UnknownRecord, path: string, ids: Set<string>): void {
  exact(section, ['id', 'capability', 'role', 'title', 'section'], path);
  registerId(section.id, `${path}.id`, ids);
  validateRole(section, path);
  string(section.title, `${path}.title`);
  validateFmSection(section.section, `${path}.section`);
  assertArchetypePaceCapabilities('FmProductPage', section.section, `${path}.section`);
  registerId(record(section.section, `${path}.section`).id, `${path}.section.id`, ids);
}

function validateSection(value: unknown, path: string, ids: Set<string>): void {
  const section = record(value, path);
  const capability = string(section.capability, `${path}.capability`);
  if (!FM_PRODUCT_CAPABILITIES.includes(capability as (typeof FM_PRODUCT_CAPABILITIES)[number])) {
    fail(`${path}.capability`, `is not registered: "${capability}"`);
  }
  if (capability === 'section') {
    validateSharedSection(section, path, ids);
    return;
  }
  const status = validateState(section.state, `${path}.state`, ids);
  const available = AVAILABLE_STATES.has(status);
  exact(
    section,
    [
      'id',
      'capability',
      'role',
      'title',
      'state',
      'evidence',
      ...(available ? (PAYLOAD_KEYS[capability] ?? []) : []),
    ],
    path,
  );
  registerId(section.id, `${path}.id`, ids);
  validateRole(section, path);
  string(section.title, `${path}.title`);
  if (section.evidence !== undefined) assertFmEvidenceDescriptor(section.evidence);
  if (!available) return;
  switch (capability) {
    case 'registered-media':
      validateRegisteredMedia(section, path, ids);
      break;
    case 'contextual-actions':
      validateActions(section, path, ids);
      break;
    case 'catalog-decision-cta':
      validateCatalog(section, path, ids);
      break;
    default:
      fail(`${path}.capability`, 'is not registered');
  }
}

const HERO_KEYS = [
  'id',
  'state',
  'eyebrow',
  'title',
  'summary',
  'proofPoints',
  'mediaIdentity',
  'slideMediaIdentities',
  'slideCopy',
  'carouselPlayLabel',
  'carouselPauseLabel',
  'priceTrend',
  'showcaseMediaIdentity',
  'showcaseMediaLabel',
  'forecast',
  'primaryAction',
  'secondaryAction',
  'leadInteraction',
  'nextSectionLabel',
] as const;

function validateHeroSlideMediaIdentities(hero: UnknownRecord, path: string): void {
  if (hero.slideMediaIdentities !== undefined) {
    const slides = record(hero.slideMediaIdentities, `${path}.slideMediaIdentities`);
    exact(slides, ['growth', 'choices', 'harvest-income'], `${path}.slideMediaIdentities`);
    for (const key of ['growth', 'choices', 'harvest-income'] as const) {
      const identity = string(slides[key], `${path}.slideMediaIdentities.${key}`);
      if (!hasMediaAsset(identity))
        fail(`${path}.slideMediaIdentities.${key}`, 'must be registered');
    }
  }
}

function validateHeroShowcase(hero: UnknownRecord, path: string): void {
  validateHeroSlideMediaIdentities(hero, path);
  if (hero.slideCopy !== undefined) {
    const slides = record(hero.slideCopy, `${path}.slideCopy`);
    exact(slides, ['growth', 'choices', 'harvest-income'], `${path}.slideCopy`);
    for (const key of ['growth', 'choices', 'harvest-income'] as const) {
      const slide = record(slides[key], `${path}.slideCopy.${key}`);
      exact(slide, ['title', 'summary', 'proofPoints'], `${path}.slideCopy.${key}`);
      string(slide.title, `${path}.slideCopy.${key}.title`);
      string(slide.summary, `${path}.slideCopy.${key}.summary`);
      array(slide.proofPoints, `${path}.slideCopy.${key}.proofPoints`).forEach((point, index) =>
        string(point, `${path}.slideCopy.${key}.proofPoints[${index}]`),
      );
    }
  }
  optionalString(hero.carouselPlayLabel, `${path}.carouselPlayLabel`);
  optionalString(hero.carouselPauseLabel, `${path}.carouselPauseLabel`);
  if (hero.priceTrend !== undefined) {
    const trend = record(hero.priceTrend, `${path}.priceTrend`);
    exact(
      trend,
      ['points', 'regionLabel', 'sourceLabel', 'pineLabel', 'pulpLabel'],
      `${path}.priceTrend`,
    );
    array(trend.points, `${path}.priceTrend.points`);
    for (const key of ['regionLabel', 'sourceLabel', 'pineLabel', 'pulpLabel'] as const) {
      string(trend[key], `${path}.priceTrend.${key}`);
    }
  }
  if (hero.showcaseMediaIdentity !== undefined) {
    const identity = string(hero.showcaseMediaIdentity, `${path}.showcaseMediaIdentity`);
    if (!hasMediaAsset(identity)) fail(`${path}.showcaseMediaIdentity`, 'must be registered');
  }
  optionalString(hero.showcaseMediaLabel, `${path}.showcaseMediaLabel`);
  if (hero.showcaseMediaLabel !== undefined && hero.showcaseMediaIdentity === undefined) {
    fail(`${path}.showcaseMediaLabel`, 'requires showcaseMediaIdentity');
  }
  if (hero.forecast === undefined) return;
  const forecast = record(hero.forecast, `${path}.forecast`);
  exact(
    forecast,
    forecast.status === 'ready'
      ? [
          'status',
          'propertyId',
          'sourcePlanLabel',
          'scopeLabel',
          'displayLocale',
          'copy',
          'activeMetric',
          'onMetricChange',
          'growth',
          'harvestIncome',
          'alternatives',
        ]
      : ['status', 'propertyId', 'copy'],
    `${path}.forecast`,
  );
  if (!['loading', 'ready', 'unavailable'].includes(String(forecast.status))) {
    fail(`${path}.forecast.status`, 'must be loading, ready, or unavailable');
  }
  string(forecast.propertyId, `${path}.forecast.propertyId`);
  const copy = record(forecast.copy, `${path}.forecast.copy`);
  exact(
    copy,
    [
      'propertyLabel',
      'modelledLabel',
      'growthLabel',
      'harvestIncomeLabel',
      'growthNavLabel',
      'choicesNavLabel',
      'choicesValueLabel',
      'choicesNearTermLabel',
      'choicesAvoidClearfellLabel',
      'choicesTapioLabel',
      'choicesPlanLabel',
      'choicesNpvLabel',
      'choicesNetLabel',
      'choicesTableCaption',
      'unavailableValueLabel',
      'incomeNavLabel',
      'loadingLabel',
      'loadingDetail',
      'unavailableLabel',
      'unavailableDetail',
    ],
    `${path}.forecast.copy`,
  );
  Object.keys(copy).forEach((key) => string(copy[key], `${path}.forecast.copy.${key}`));
  if (forecast.status === 'ready') validateReadyHeroForecast(forecast, path);
}

function validateReadyHeroForecast(forecast: UnknownRecord, path: string): void {
  string(forecast.sourcePlanLabel, `${path}.forecast.sourcePlanLabel`);
  string(forecast.scopeLabel, `${path}.forecast.scopeLabel`);
  string(forecast.displayLocale, `${path}.forecast.displayLocale`);
  if (!['growth', 'harvest-income'].includes(String(forecast.activeMetric))) {
    fail(`${path}.forecast.activeMetric`, 'must be growth or harvest-income');
  }
  if (typeof forecast.onMetricChange !== 'function') {
    fail(`${path}.forecast.onMetricChange`, 'must be a callback');
  }
  for (const key of ['growth', 'harvestIncome'] as const) {
    const series = record(forecast[key], `${path}.forecast.${key}`);
    exact(series, ['unit', 'points'], `${path}.forecast.${key}`);
    string(series.unit, `${path}.forecast.${key}.unit`);
    array(series.points, `${path}.forecast.${key}.points`).forEach((point, index) => {
      const pointPath = `${path}.forecast.${key}.points[${index}]`;
      const row = record(point, pointPath);
      exact(row, ['year', 'value'], pointPath);
      finiteNumber(row.year, `${pointPath}.year`);
      finiteNumber(row.value, `${pointPath}.value`);
    });
  }
  array(forecast.alternatives, `${path}.forecast.alternatives`).forEach((item, index) => {
    const alternative = record(item, `${path}.forecast.alternatives[${index}]`);
    exact(
      alternative,
      ['id', 'status', 'firstOperationYear', 'npvPerHectareEur', 'netCashEur', 'unavailableReason'],
      `${path}.forecast.alternatives[${index}]`,
    );
    string(alternative.id, `${path}.forecast.alternatives[${index}].id`);
    if (!['ready', 'unavailable'].includes(String(alternative.status))) {
      fail(`${path}.forecast.alternatives[${index}].status`, 'must be ready or unavailable');
    }
    for (const key of ['firstOperationYear', 'npvPerHectareEur', 'netCashEur'] as const) {
      if (alternative[key] !== null) {
        finiteNumber(alternative[key], `${path}.forecast.alternatives[${index}].${key}`);
      }
    }
    optionalString(
      alternative.unavailableReason,
      `${path}.forecast.alternatives[${index}].unavailableReason`,
    );
  });
}

function validateHeroActions(hero: UnknownRecord, path: string, ids: Set<string>): void {
  if (hero.primaryAction !== undefined) {
    validateAction(hero.primaryAction, `${path}.primaryAction`, ids);
    if ((hero.primaryAction as FmProductActionDescriptor).intent !== 'primary') {
      fail(`${path}.primaryAction.intent`, 'must be primary');
    }
  }
  if (hero.secondaryAction !== undefined && hero.primaryAction === undefined) {
    fail(`${path}.secondaryAction`, 'requires primaryAction');
  }
  if (hero.secondaryAction !== undefined) {
    validateAction(hero.secondaryAction, `${path}.secondaryAction`, ids);
  }
}

function validateHeroLeadInteraction(hero: UnknownRecord, path: string): void {
  if (hero.leadInteraction === undefined) return;
  const lead = record(hero.leadInteraction, `${path}.leadInteraction`);
  exact(
    lead,
    [
      'kind',
      'purpose',
      'label',
      'placeholder',
      'value',
      'submitLabel',
      'helperText',
      'onValueChange',
      'onSubmit',
    ],
    `${path}.leadInteraction`,
  );
  if (lead.kind !== 'search') fail(`${path}.leadInteraction.kind`, 'must be search');
  if (!['property', 'catalog'].includes(String(lead.purpose))) {
    fail(`${path}.leadInteraction.purpose`, 'must be property or catalog');
  }
  ['label', 'placeholder', 'submitLabel'].forEach((key) =>
    string(lead[key], `${path}.leadInteraction.${key}`),
  );
  if (typeof lead.value !== 'string') fail(`${path}.leadInteraction.value`, 'must be a string');
  optionalString(lead.helperText, `${path}.leadInteraction.helperText`);
  if (typeof lead.onValueChange !== 'function')
    fail(`${path}.leadInteraction.onValueChange`, 'must be a callback');
  if (typeof lead.onSubmit !== 'function')
    fail(`${path}.leadInteraction.onSubmit`, 'must be a callback');
}

function validateHero(value: unknown, path: string, ids: Set<string>): void {
  const hero = record(value, path);
  exact(hero, HERO_KEYS, path);
  registerId(hero.id, `${path}.id`, ids);
  validateState(hero.state, `${path}.state`, ids);
  optionalString(hero.eyebrow, `${path}.eyebrow`);
  string(hero.title, `${path}.title`);
  string(hero.summary, `${path}.summary`);
  const proofPoints = array(hero.proofPoints, `${path}.proofPoints`);
  if (proofPoints.length > 4) fail(`${path}.proofPoints`, 'must contain at most 4 points');
  proofPoints.forEach((point, index) => string(point, `${path}.proofPoints[${index}]`));
  const mediaIdentity = string(hero.mediaIdentity, `${path}.mediaIdentity`);
  if (!hasMediaAsset(mediaIdentity)) fail(`${path}.mediaIdentity`, 'must be registered');
  validateHeroShowcase(hero, path);
  validateHeroActions(hero, path, ids);
  validateHeroLeadInteraction(hero, path);
  string(hero.nextSectionLabel, `${path}.nextSectionLabel`);
}

function validateFooter(value: unknown, path: string): void {
  const footer = record(value, path);
  exact(
    footer,
    [
      'brandLabel',
      'description',
      'legalAriaLabel',
      'termsLabel',
      'privacyLabel',
      'contactLabel',
      'contactEmail',
      'buyerLabel',
      'onBuyer',
      'allRightsReserved',
    ],
    path,
  );
  [
    'brandLabel',
    'description',
    'legalAriaLabel',
    'termsLabel',
    'privacyLabel',
    'contactLabel',
    'contactEmail',
    'buyerLabel',
    'allRightsReserved',
  ].forEach((key) => string(footer[key], `${path}.${key}`));
  if (typeof footer.onBuyer !== 'function') fail(`${path}.onBuyer`, 'must be a callback');
}

function validateChapter(
  value: unknown,
  path: string,
  ids: Set<string>,
): { chapter: UnknownRecord; id: string; mobileOrder: number } {
  const chapter = record(value, path);
  exact(
    chapter,
    [
      'id',
      'ownerQuestion',
      'literalAnswer',
      'dominantSectionId',
      'transition',
      'mobileOrder',
      'conversionRole',
      'sections',
    ],
    path,
  );
  const id = registerId(chapter.id, `${path}.id`, ids);
  string(chapter.ownerQuestion, `${path}.ownerQuestion`);
  string(chapter.literalAnswer, `${path}.literalAnswer`);
  const dominantSectionId = string(chapter.dominantSectionId, `${path}.dominantSectionId`);
  const transition = record(chapter.transition, `${path}.transition`);
  exact(transition, ['label', 'targetChapterId'], `${path}.transition`);
  string(transition.label, `${path}.transition.label`);
  optionalString(transition.targetChapterId, `${path}.transition.targetChapterId`);
  const mobileOrder = finiteNumber(chapter.mobileOrder, `${path}.mobileOrder`);
  if (!Number.isInteger(mobileOrder) || mobileOrder < 1)
    fail(`${path}.mobileOrder`, 'must be a positive integer');
  if (!CONVERSION_ROLES.has(String(chapter.conversionRole)))
    fail(`${path}.conversionRole`, 'is not registered');
  const sections = array(chapter.sections, `${path}.sections`);
  sections.forEach((section, index) => validateSection(section, `${path}.sections[${index}]`, ids));
  const sectionIds = sections.map((section) => record(section, `${path}.sections`).id);
  if (!sectionIds.includes(dominantSectionId))
    fail(`${path}.dominantSectionId`, 'must reference a section of this chapter');
  const roleOrder = sections.map((section) =>
    SECTION_ROLES.indexOf(
      record(section, `${path}.sections`).role as (typeof SECTION_ROLES)[number],
    ),
  );
  if (roleOrder.some((role, index) => index > 0 && role < roleOrder[index - 1])) {
    fail(`${path}.sections`, 'must follow lead, evidence, detail, action reading order');
  }
  return { chapter, id, mobileOrder };
}

export function validateFmProductPageProps(value: unknown): asserts value is FmProductPageProps {
  const props = record(value, 'props');
  exact(props, ['chrome', 'footer', 'hero', 'chapters', 'ariaLabel', 'data-testid'], 'props');
  const ids = new Set<string>();
  validateHero(props.hero, 'props.hero', ids);
  string(props.ariaLabel, 'props.ariaLabel');
  optionalString(props['data-testid'], 'props.data-testid');
  const chapters = array(props.chapters, 'props.chapters').map((chapter, index) =>
    validateChapter(chapter, `props.chapters[${index}]`, ids),
  );
  const chapterIds = new Set(chapters.map((item) => item.id));
  if (props.chrome !== undefined) {
    validateFmProductChrome(props.chrome, 'props.chrome', ids, chapterIds);
  }
  if (props.footer !== undefined) validateFooter(props.footer, 'props.footer');
  const mobileOrders = new Set<number>();
  chapters.forEach(({ chapter, id, mobileOrder }, index) => {
    if (mobileOrders.has(mobileOrder))
      fail(`props.chapters[${index}].mobileOrder`, 'must be unique');
    mobileOrders.add(mobileOrder);
    const target = record(
      chapter.transition,
      `props.chapters[${index}].transition`,
    ).targetChapterId;
    if (target !== undefined && (!chapterIds.has(String(target)) || target === id)) {
      fail(`props.chapters[${index}].transition.targetChapterId`, 'must reference another chapter');
    }
  });
}

export function validateFmProductHeroDescriptor(
  value: unknown,
): asserts value is FmProductHeroDescriptor {
  validateHero(value, 'hero', new Set<string>());
}
